import { List } from '@react-admin/ra-rbac';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';
import { useExpandAll } from 'ra-core';
import {
  Button,
  DataTable,
  SelectInput,
  TextInput,
  useCreatePath,
  useGetOne,
  useListContext,
  useResourceContext,
  useResourceDefinitions,
} from 'react-admin';
import { ReactElement } from 'react';
import ExtendedListActions from '../../Components/ExtendedListActions';
import { ActionLogResource } from '../../Entities/ActionLog';
import { Link } from 'react-router-dom';
import TzDateField from '../../Components/TzDate/TzDateField';
import TzDateTimeInput from '../../Components/TzDate/TzDateTimeInput';
import LargePagination from '../../Components/LargePagination';
import { isJsonApiHasOneRelationship, JsonApiResource } from '../../DataProvider/JsonApiTypes';
import { DEFAULT_LIST_PAGE_PAGINATION_LIMIT } from '../../utils';
import ChangesPanel from './ChangesPanel';
import { ACTION_CHOICES, ENTITY_TYPE_CHOICES, ENTITY_TYPE_LABELS } from './utils';

const CHANGE_DETAILS_ENTITY_TYPES = new Set(['products', 'offers', 'mapping_rules']);
const CHANGE_DETAILS_DESCRIPTION =
  'Change details are recorded only for update actions on Products, Offers, and Mapping Rules.';

function getSpecialEntityQueryConfig(
  entityType?: string | null,
): { resource: string; prefetch: string[] } | undefined {
  switch (entityType) {
    case 'labels':
      return { resource: 'labels', prefetch: ['category'] };
    case 'label_values':
      return { resource: 'label_values', prefetch: ['label'] };
    case 'merchant_remarks':
      return { resource: 'merchant_remarks', prefetch: ['merchant'] };
    case 'feed_fields':
      return { resource: 'feed_fields', prefetch: ['feed'] };
    default:
      return undefined;
  }
}

function getSpecialEntityLinkTarget(
  entityType: string,
  entityId: string,
  specialEntityRecord?: JsonApiResource,
  labelFromValueRecord?: JsonApiResource,
): { resource: string; id: string; tab: string } | undefined {
  switch (entityType) {
    case 'product_labels':
      return { resource: 'products', id: entityId.split(':', 2)[0], tab: '1' };
    case 'labels': {
      const categoryRelationship = specialEntityRecord?.relationships?.category;
      const categoryId = isJsonApiHasOneRelationship(categoryRelationship)
        ? categoryRelationship.data?.id
        : undefined;
      return categoryId ? { resource: 'categories', id: categoryId, tab: '1' } : undefined;
    }
    case 'label_values': {
      const categoryRelationship = labelFromValueRecord?.relationships?.category;
      const categoryId = isJsonApiHasOneRelationship(categoryRelationship)
        ? categoryRelationship.data?.id
        : undefined;
      return categoryId ? { resource: 'categories', id: categoryId, tab: '1' } : undefined;
    }
    case 'merchant_remarks': {
      const merchantRelationship = specialEntityRecord?.relationships?.merchant;
      const merchantId = isJsonApiHasOneRelationship(merchantRelationship)
        ? merchantRelationship.data?.id
        : undefined;
      return merchantId ? { resource: 'merchants', id: merchantId, tab: '6' } : undefined;
    }
    case 'feed_fields': {
      const feedRelationship = specialEntityRecord?.relationships?.feed;
      const feedId = isJsonApiHasOneRelationship(feedRelationship)
        ? feedRelationship.data?.id
        : undefined;
      return feedId ? { resource: 'feeds', id: feedId, tab: '1' } : undefined;
    }
    default:
      return undefined;
  }
}

function ActionEntityIdLink({
  entityId,
  entityType,
}: {
  entityId?: string | null;
  entityType?: string | null;
}): ReactElement | string {
  const createPath = useCreatePath();
  const resourceDefinitions = useResourceDefinitions();
  const specialEntityQueryConfig = getSpecialEntityQueryConfig(entityType);
  const specialEntityQuery = useGetOne<JsonApiResource>(
    specialEntityQueryConfig?.resource ?? 'action_logs',
    { id: entityId ?? '', meta: { prefetch: specialEntityQueryConfig?.prefetch ?? [] } },
    { enabled: Boolean(entityId) && Boolean(specialEntityQueryConfig) },
  );
  const labelRelationship = specialEntityQuery.data?.relationships?.label;
  const labelId = isJsonApiHasOneRelationship(labelRelationship)
    ? labelRelationship.data?.id
    : undefined;
  const labelFromValueQuery = useGetOne<JsonApiResource>(
    'labels',
    { id: labelId ?? '', meta: { prefetch: ['category'] } },
    { enabled: entityType === 'label_values' && Boolean(labelId) },
  );

  if (!entityId) {
    return '-';
  }

  if (!entityType) {
    return entityId;
  }

  const resourceDefinition = resourceDefinitions[entityType];

  if (resourceDefinition?.hasEdit) {
    return (
      <Link to={createPath({ resource: entityType, type: 'edit', id: entityId })}>{entityId}</Link>
    );
  }

  if (resourceDefinition?.hasShow) {
    return (
      <Link to={createPath({ resource: entityType, type: 'show', id: entityId })}>{entityId}</Link>
    );
  }

  const createEntityEditTabPath = (resource: string, id: string, tab: string): string =>
    `${createPath({ resource, type: 'edit', id })}/${tab}`;

  const specialEntityLinkTarget = getSpecialEntityLinkTarget(
    entityType,
    entityId,
    specialEntityQuery.data,
    labelFromValueQuery.data,
  );

  if (specialEntityLinkTarget) {
    return (
      <Link
        to={createEntityEditTabPath(
          specialEntityLinkTarget.resource,
          specialEntityLinkTarget.id,
          specialEntityLinkTarget.tab,
        )}
      >
        {entityId}
      </Link>
    );
  }

  return entityId;
}

const filters: ReactElement[] = [
  <SelectInput
    key="entity_type"
    label="Entity"
    source="equals.entity_type"
    choices={ENTITY_TYPE_CHOICES}
  />,
  <TextInput key="entity_id" label="Entity ID" source="equals.entity_id" />,
  <TextInput key="user_email" label="User email" source="equals.user_identifier" />,
  <SelectInput key="action" label="Action" source="equals.action" choices={ACTION_CHOICES} />,
  <TzDateTimeInput
    key="occurred_at_utc_after"
    label="Occurred After"
    source="greaterOrEqual.occurred_at_utc"
  />,
  <TzDateTimeInput
    key="occurred_at_utc_before"
    label="Occurred Before"
    source="lessOrEqual.occurred_at_utc"
  />,
];

function ExpandAllRowsButton(): ReactElement | null {
  const { data } = useListContext<ActionLogResource>();
  const resource = useResourceContext();
  const expandableIds = (data ?? [])
    .filter(
      (record) =>
        record.attributes?.action === 'update' &&
        CHANGE_DETAILS_ENTITY_TYPES.has(record.attributes?.entity_type ?? ''),
    )
    .map((record) => record.id);
  const [expanded, toggleExpanded] = useExpandAll(resource ?? '', expandableIds);

  if (!resource) {
    return null;
  }

  return (
    <Button
      label={expanded ? 'Collapse all changes' : 'Expand all changes'}
      disabled={expandableIds.length === 0}
      onClick={toggleExpanded}
      size="small"
    >
      <ExpandMoreIcon />
    </Button>
  );
}

function ActionLogPagination(): ReactElement {
  return (
    <Box
      alignItems="center"
      display="flex"
      gap={2}
      justifyContent="space-between"
      px={2}
      sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
    >
      <Box alignItems="center" display="flex" gap={0.5} sx={{ color: 'text.secondary' }}>
        <InfoOutlinedIcon fontSize="small" />
        <Typography variant="body2">{CHANGE_DETAILS_DESCRIPTION}</Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <LargePagination />
      </Box>
    </Box>
  );
}

export default function ActionLogList(): ReactElement {
  const createPath = useCreatePath();
  const resourceDefinitions = useResourceDefinitions();

  return (
    <List
      actions={
        <ExtendedListActions
          customActions={[<ExpandAllRowsButton key="expand-all-rows" />]}
          hasFilters
        />
      }
      filters={filters}
      pagination={<ActionLogPagination />}
      perPage={DEFAULT_LIST_PAGE_PAGINATION_LIMIT}
      sort={{ field: 'occurred_at_utc', order: 'DESC' }}
    >
      <DataTable
        expand={<ChangesPanel />}
        isRowExpandable={(record) =>
          record.attributes?.action === 'update' &&
          CHANGE_DETAILS_ENTITY_TYPES.has(record.attributes?.entity_type ?? '')
        }
      >
        <DataTable.Col source="id" label="ID" />
        <DataTable.Col source="attributes.occurred_at_utc" label="Occurred At">
          <TzDateField source="attributes.occurred_at_utc" serverTimezone="UTC" showTime />
        </DataTable.Col>
        <DataTable.Col
          source="attributes.entity_type"
          label="Entity"
          render={(record: ActionLogResource) => {
            const entityType = record?.attributes?.entity_type;

            if (!entityType) {
              return <i>Unknown</i>;
            }

            const resourceDefinition = resourceDefinitions[entityType];
            const label =
              resourceDefinition?.options?.label ??
              ENTITY_TYPE_LABELS.get(entityType) ??
              entityType;

            if (!resourceDefinition?.hasList) {
              return label;
            }

            return <Link to={createPath({ resource: entityType, type: 'list' })}>{label}</Link>;
          }}
        />
        <DataTable.Col
          source="attributes.entity_id"
          label="Entity ID"
          render={(record: ActionLogResource) => {
            const entityType = record?.attributes?.entity_type;
            const entityId = record?.attributes?.entity_id;
            return <ActionEntityIdLink entityType={entityType} entityId={entityId} />;
          }}
        />
        <DataTable.Col source="attributes.action" label="Action" />
        <DataTable.Col
          source="attributes.user_identifier"
          label="User"
          render={(record: ActionLogResource) => {
            const userId = record?.attributes?.user_id;
            const userIdentifier = record?.attributes?.user_identifier;

            if (!userId) {
              return <i>Unknown</i>;
            }

            return (
              <a
                href={`https://manage.auth0.com/dashboard/eu/futureplc/users/${btoa(userId)}`}
                target="_blank"
                rel="noopener noreferrer"
                title={userId}
              >
                {userIdentifier ?? userId}
              </a>
            );
          }}
        />
      </DataTable>
    </List>
  );
}
